import {Injectable, Injector} from '@angular/core';
import { Observable, throwError } from 'rxjs';
import {catchError, filter, switchMap} from 'rxjs/operators';
import {ApFindControlConfig} from './ap-findcontrol-config';
import {
  FindControlAdditionalSearchParametersDto,
  FindControlRequiredSearchParametersDto,
  FindControlResultDto,
  HiddenActionItemType,
  IColumnPk,
} from 'webcommon/shared';
import {ApModalService, ApModalResult} from 'webcommon/core-ui';
import { ApFindControlService } from './ap-findcontrol.service';
import { FindControlMode } from '../models/index';

@Injectable({
  providedIn: 'root',
})
export class ApFindControlModalService {
  constructor(
    private apModalService: ApModalService,
    private apFindControlService: ApFindControlService,
  ) {
  }

  openModal(
    elementInjector: Injector,
    findControlId: string,
    preselectedItems: IColumnPk[],
    baseId: string,
    findControlDisplayName: string,
    findControlMode: FindControlMode = FindControlMode.MultiSelect,
    requiredSearchParameters?: FindControlRequiredSearchParametersDto,
    additionalSearchParameters?: FindControlAdditionalSearchParametersDto,
    hiddenActionItems?: HiddenActionItemType[],
  ): Observable<ApModalResult<IColumnPk[]>> {
    const config = elementInjector.get(ApFindControlConfig);

    const findControlModalType =
      config.findControlModalById.get(findControlId) ||
      config.defaultModal;

    const initialState = {
      additionalSearchParameters,
      baseId,
      preselectedItems,
      findControlDisplayName,
      findControlId,
      requiredSearchParameters,
      mode: findControlMode,
      hiddenActionItems,
    };

    const providers = findControlModalType.modalTokens ?
      this.apModalService.provideClasses(elementInjector, ...findControlModalType.modalTokens) :
      undefined;

    return this.apModalService.showComponentModal$(findControlModalType, {
      class: 'emds-modal-medium',
      ignoreBackdropClick: true,
      initialState,
      providers,
    }).pipe(
      catchError((e) => {
        const messagePrefix = `Error from FindControl '${findControlId}'`;
        if (e instanceof Error) {
          e.message = `${messagePrefix}: ${e.message}`;
        } else {
          console.error(messagePrefix, e);
        }
        return throwError(e);
      })
    );
  }

  openFindControlModalSelection(
    elementInjector: Injector,
    findControlId: string,
    guids: string[],
    elmId: string,
    findControlDisplayName: string,
    findControlMode: FindControlMode = FindControlMode.MultiSelect,
    requiredSearchParameters?: FindControlRequiredSearchParametersDto,
    additionalSearchParameters?: FindControlAdditionalSearchParametersDto,
  ): Observable<ApModalResult<IColumnPk[]>> {
    if (guids.length) {
      return this.apFindControlService.getSelectedItems(findControlId, guids).pipe(
        switchMap((data) => this.displayFindControlModal(
          elementInjector,
          findControlId,
          data,
          elmId,
          findControlDisplayName,
          findControlMode,
          requiredSearchParameters,
          additionalSearchParameters,
        )),
        filter((result) => result.isSuccess),
      );
    } else {
      return this.displayFindControlModal(
          elementInjector,
          findControlId,
          undefined,
          elmId,
          findControlDisplayName,
          findControlMode,
          requiredSearchParameters,
          additionalSearchParameters,
        ).pipe(
        filter((result) => result.isSuccess),
      );
    }
  }

  private displayFindControlModal(
    elementInjector: Injector,
    findControlId: string,
    findControlResult: FindControlResultDto | undefined,
    elmId: string,
    findControlDisplayName: string,
    findControlMode: FindControlMode,
    requiredSearchParameters?: FindControlRequiredSearchParametersDto,
    additionalSearchParameters?: FindControlAdditionalSearchParametersDto,
  ): Observable<ApModalResult<IColumnPk[]>>
  {
    const preselectedItems = findControlResult?.Rows || [];

    return this.openModal(
      elementInjector,
      findControlId,
      preselectedItems,
      elmId,
      findControlDisplayName,
      findControlMode,
      requiredSearchParameters,
      additionalSearchParameters,
    );
  }
}
